/**
 * The 80 mm thermal receipt: a simplified tax invoice small enough for a
 * counter printer or a van's Bluetooth printer.
 *
 * Built as a self-contained HTML page and printed from a hidden iframe, so
 * it needs no route, no second login and no print stylesheet fighting the
 * app's own: the browser's print dialog sees only the receipt. ZATCA
 * Phase 1 asks a simplified invoice for the seller's name and VAT number,
 * the date and time, the total with VAT, the VAT, and the QR -- all here.
 */
import type { CompanyInfo, SaleDetail, TransferDetail } from '../api/types'

const esc = (v: unknown) => String(v ?? '').replace(/[&<>"]/g, (ch) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]!))

const money = (v: string | number) =>
  Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function receiptHtml(s: SaleDetail): string {
  const ret = s.kind === 'RETURN'
  const title = ret ? ['Credit Note', 'إشعار دائن']
    : s.invoice_type === 'STANDARD' ? ['Tax Invoice', 'فاتورة ضريبية']
      : ['Simplified Tax Invoice', 'فاتورة ضريبية مبسطة']
  const when = s.created_at ? new Date(s.created_at + (s.created_at.endsWith('Z') ? '' : 'Z')) : null
  const time = when ? when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit',
                                                         timeZone: 'Asia/Riyadh' }) : ''
  const lines = s.lines.map((l) => `
    <div class="ln">
      <div class="nm">${esc(l.item_name)}${l.item_name_ar ? `<div class="ar">${esc(l.item_name_ar)}</div>` : ''}</div>
      <div class="row"><span>${esc(l.qty)} ${esc(l.unit)} × ${money(l.price)}${Number(l.discount) ? ` − ${money(l.discount)}` : ''}</span>
        <b>${money(Number(l.net) + Number(l.vat_amount))}</b></div>
    </div>`).join('')
  const seller = s.seller
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(s.doc_no)}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; }
  body { width: 74mm; margin: 0 auto; font: 11px/1.35 'IBM Plex Sans', 'Segoe UI', Tahoma, sans-serif;
         color: #000; }
  .c { text-align: center; }
  .ar { direction: rtl; font-family: 'IBM Plex Sans Arabic', Tahoma, sans-serif; }
  h1 { font-size: 13px; margin: 4px 0 0; }
  .muted { color: #333; font-size: 10px; }
  .t { font-weight: 700; font-size: 12px; margin: 6px 0 2px; border-top: 1px dashed #000;
       border-bottom: 1px dashed #000; padding: 4px 0; }
  .row { display: flex; justify-content: space-between; gap: 6px; }
  .ln { padding: 3px 0; border-bottom: 1px dotted #999; }
  .nm { font-weight: 600; }
  .tot { margin-top: 4px; }
  .tot .row { padding: 1px 0; }
  .grand { font-size: 14px; font-weight: 700; border-top: 1px solid #000; margin-top: 2px; padding-top: 3px; }
  .qr { display: block; width: 34mm; height: 34mm; margin: 6px auto 2px; }
  .void { font-weight: 700; font-size: 14px; border: 2px solid #000; padding: 2px; margin: 4px 0; }
</style></head><body>
  <div class="c">
    <h1>${esc(seller.name)}</h1>
    ${seller.name_ar ? `<div class="ar">${esc(seller.name_ar)}</div>` : ''}
    ${seller.address ? `<div class="muted">${esc(seller.address)}</div>` : ''}
    ${seller.vat_number ? `<div>VAT No / الرقم الضريبي: <b>${esc(seller.vat_number)}</b></div>` : ''}
    ${seller.cr_number ? `<div class="muted">CR ${esc(seller.cr_number)}</div>` : ''}
    <div class="t">${title[0]}<div class="ar">${title[1]}</div></div>
    ${s.status === 'CANCELLED' ? '<div class="void">CANCELLED · ملغاة</div>' : ''}
  </div>
  <div class="row"><span>No / رقم</span><b>${esc(s.doc_no)}</b></div>
  <div class="row"><span>Date / التاريخ</span><span>${esc(s.doc_date)} ${esc(time)}</span></div>
  ${s.against_no ? `<div class="row"><span>Against / مقابل</span><span>${esc(s.against_no)}</span></div>` : ''}
  <div class="row"><span>Customer / العميل</span><span>${esc(s.customer_name || 'Cash customer')}</span></div>
  ${s.customer_vat ? `<div class="row"><span>Buyer VAT</span><span>${esc(s.customer_vat)}</span></div>` : ''}
  <div style="margin-top:4px">${lines}</div>
  <div class="tot">
    <div class="row"><span>Total excl. VAT / الإجمالي قبل الضريبة</span><span>${money(s.subtotal)}</span></div>
    <div class="row"><span>VAT 15% / ضريبة القيمة المضافة</span><span>${money(s.vat_amount)}</span></div>
    <div class="row grand"><span>Total / الإجمالي (SAR)</span><span>${money(s.total)}</span></div>
    ${s.paid_by ? `<div class="row muted"><span>Paid / مدفوع</span><span>${esc(s.paid_by)}</span></div>` : ''}
  </div>
  ${s.qr_data_url ? `<img class="qr" src="${s.qr_data_url}" alt="">` : ''}
  <div class="c muted">Thank you · شكراً لكم</div>
</body></html>`
}

/** Print the receipt without leaving the page. */
export function printReceipt(s: SaleDetail): void {
  printHtml(receiptHtml(s))
}

/**
 * The 80 mm load sheet: what went onto the van, for the driver to check
 * and sign. Quantities as they were loaded, with the piece count beside.
 */
export function loadSheetHtml(x: TransferDetail, co?: Pick<CompanyInfo, 'name_en' | 'name_ar'>
  | null): string {
  const lines = x.lines.map((l) => `
    <div class="ln">
      <div class="nm">${esc(l.item_code)} · ${esc(l.item_name)}${l.item_name_ar ? `<div class="ar">${esc(l.item_name_ar)}</div>` : ''}</div>
      <div class="row"><span>${esc(l.base_qty)} ${esc(l.base_unit)}</span><b>${esc(l.qty)} ${esc(l.unit)}</b></div>
    </div>`).join('')
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(x.doc_no)}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; }
  body { width: 74mm; margin: 0 auto; font: 11px/1.35 'IBM Plex Sans', 'Segoe UI', Tahoma, sans-serif; color: #000; }
  .c { text-align: center; }
  .ar { direction: rtl; font-family: 'IBM Plex Sans Arabic', Tahoma, sans-serif; }
  h1 { font-size: 13px; margin: 4px 0 0; }
  .t { font-weight: 700; font-size: 12px; margin: 6px 0 2px; border-top: 1px dashed #000;
       border-bottom: 1px dashed #000; padding: 4px 0; }
  .row { display: flex; justify-content: space-between; gap: 6px; }
  .ln { padding: 3px 0; border-bottom: 1px dotted #999; }
  .nm { font-weight: 600; }
  .void { font-weight: 700; font-size: 14px; border: 2px solid #000; padding: 2px; margin: 4px 0; }
  .sig { margin-top: 18px; display: flex; gap: 8px; }
  .sig div { flex: 1; border-top: 1px solid #000; padding-top: 2px; text-align: center; font-size: 10px; }
</style></head><body>
  <div class="c">
    ${co?.name_en ? `<h1>${esc(co.name_en)}</h1>` : ''}
    ${co?.name_ar ? `<div class="ar">${esc(co.name_ar)}</div>` : ''}
    <div class="t">Stock transfer<div class="ar">تحويل مخزون</div></div>
    ${x.status === 'CANCELLED' ? '<div class="void">CANCELLED · ملغى</div>' : ''}
  </div>
  <div class="row"><span>No / رقم</span><b>${esc(x.doc_no)}</b></div>
  <div class="row"><span>Date / التاريخ</span><span>${esc(x.doc_date)}</span></div>
  <div class="row"><span>From / من</span><span>${esc(x.from_store_name)}</span></div>
  <div class="row"><span>To / إلى</span><span>${esc(x.to_store_name)}</span></div>
  <div style="margin-top:4px">${lines}</div>
  <div class="row" style="margin-top:4px"><span>Lines / الأصناف</span><b>${x.lines.length}</b></div>
  ${x.notes ? `<div style="margin-top:4px">${esc(x.notes)}</div>` : ''}
  <div class="sig"><div>Issued by · سلّمه</div><div>Received by · استلمه</div></div>
</body></html>`
}

/** Print a self-contained page from a hidden frame, without leaving the screen. */
export function printHtml(html: string): void {
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0',
                               height: '0', border: '0' })
  document.body.appendChild(frame)
  const doc = frame.contentDocument!
  doc.open(); doc.write(html); doc.close()
  const go = () => {
    frame.contentWindow?.focus()
    frame.contentWindow?.print()
    setTimeout(() => frame.remove(), 1500)
  }
  // Give images (the QR) a moment to decode before the print dialog opens.
  setTimeout(go, 250)
}
